import api from "../../config/api";
import { store } from "../../redux/store";
import {
  ChatRequest,
  StreamChunk,
  ChatThreadListResponse,
  ChatThreadDetailResponse,
  CasesResponse,
} from "../../interfaces/api/chat";
import { ChatType } from "../../shared/utils/enums";
import http from "../http";
import { ConfigResponse } from "../../interfaces/api/chat/config";

declare global {
  interface Window {
    chatStreamController?: AbortController;
  }
}

const chat = {
  /**
   * Get list of chat threads
   * @param type - The type of chat threads to fetch
   */
  getChatThreads: async (
    type: ChatType = ChatType.QNA,
  ): Promise<ChatThreadListResponse> => {
    return http.get(`${api.threads}`, { type });
  },

  /**
   * Get details of a specific chat thread
   * @param threadId - The ID of the thread to fetch
   */
  getChatThreadDetails: async (
    threadId: number,
  ): Promise<ChatThreadDetailResponse> => {
    return http.get(`${api.threads}/${threadId}`);
  },

  /**
   * Get details of a specific chat thread
   * @param threadId - The ID of the thread to fetch
   */
  getConfig: async (): Promise<ConfigResponse> => {
    return http.get(api.config);
  },
  /**
   * Submit a chat streaming request
   * @param request - The chat request parameters
   * @param onChunk - Callback for each chunk of the stream
   * @param onComplete - Callback when the stream is complete
   * @param onError - Callback when an error occurs
   */
  submitChatStreaming: async (
    request: ChatRequest,
    onChunk: (chunk: string, threadId?: string, title?: string) => void,
    onComplete: () => void,
    onError: (error: string) => void,
  ): Promise<void> => {
    let abortController: AbortController | null = null;

    try {
      // Abort any previous stream
      if (window.chatStreamController) {
        window.chatStreamController.abort();
      }

      abortController = new AbortController();
      window.chatStreamController = abortController;

      // Get the auth token from Redux store
      const state = store.getState();
      const token = state.auth.currentUser?.token;
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await fetch(api.ask, {
        signal: abortController.signal,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(request),
      });

      if (response.status === 401) {
        throw new Error("Unauthorized access. Please check your API token.");
      }

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      if (!response.body) {
        throw new Error("ReadableStream not supported");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const parsedObj: StreamChunk = JSON.parse(line);
            if (parsedObj.error) throw new Error(parsedObj.error);
            if (parsedObj.text || parsedObj.thread_id) {
              onChunk(
                parsedObj.text || "",
                parsedObj.thread_id,
                parsedObj.title || "",
              );
            }
          } catch (parseError) {
            onChunk(line);
          }
        }
      }

      onComplete();
    } catch (error: any) {
      if (error.name === "AbortError" || error.message.includes("aborted")) {
        return;
      }
      const errorMessage =
        error.message || "Something went wrong, please try again later!";
      onError(errorMessage);
    } finally {
      if (abortController) {
        abortController.abort();
      }
    }
  },

  /**
   * Abort the current streaming request
   */
  abortChatStreaming: (): void => {
    if (window.chatStreamController) {
      window.chatStreamController.abort();
    }
  },

  /**
   * Get list of available cases
   */
  getCases: async (): Promise<CasesResponse> => {
    return http.get(api.cases);
  },
};

export default chat;
